import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} DisableLicenseData
 * @property {number} sub_school_institution_id - Sub-school institution ID
 * @property {number} license_count - Number of licenses to disable
 * @property {string} [remarks] - Remarks for the license disable action
 */

/**
 * @description Custom hook to disable licenses for a school
 */
export const useDisableLicenseForSubSchool = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * @description Disable licenses for a school
     * @async
     * @param {DisableLicenseData} licenseData - The license disable data
     * @param {()=> void} [onSuccess] - Callback function to be called on success
     * @returns {Promise<boolean>} Resolves with true on success, false on failure
     */
    const disableLicense = async (licenseData, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            await axiosInstance.post(
                `${TENANT_DOMAIN}/organization/admin/licenses/disable`,
                {
                    sub_school_institution_id: licenseData.sub_school_institution_id,
                    license_count: licenseData.license_count,
                    ...(licenseData.remarks && { remarks: licenseData.remarks }),
                },
            );

            setSuccess(true);
            if (onSuccess) onSuccess();
            return true;
        } catch (err) {
            setSuccess(false);

            if (err?.response?.status === 500 || err?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

                setError(
                    firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
                );
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, success, disableLicense };
};
